import { useQuery, skipToken, type UseQueryResult } from "@tanstack/react-query";
import { useApiConfig, type ApiConfig } from "@visioni/a3s-client-api/app";
import { expandEndpoint } from "@visioni/a3s-client-api";
import { reportItemByIdQuery } from "@visioni/a3s-reports/app";
import {
  GetReportResponse,
  safeDecodeRoadSenseListing,
  getReportByIdEndpoint,
  type RoadSenseListingSite,
} from "@visioni/a3s-reports/client-messages";
import { useTenant, useTenantId } from "@src/features/tenants";

/**
 * Lists the sites belonging to a single RoadSense **lifecycle** (one report
 * row, identified by `reportId`).
 *
 * A RoadSense report is multi-file: one report run covers an asset's cumulative
 * hire lifecycle and produces one render `.json` + one `.xlsx` **per site**. The
 * per-site list (with each site's `payloadS3Key`/`workbookS3Key`) is stamped
 * onto the report row's `listingSummary` at generation time, so this hook
 * fetches the by-id row and decodes `listingSummary.sites` rather than calling a
 * separate sites endpoint (there isn't one — the listing summary *is* the site
 * index for the lifecycle).
 *
 * The fetch is deliberately fresh (not read from the list-query cache) so a
 * deep-link straight to `…/report/<reportId>` resolves without the lifecycle
 * list ever having been visited.
 *
 * Readiness is surfaced the same way as the report fetch:
 *  - `not_found` (404) — no row for `(tenant, "roadsense", reportId)`
 *  - `not_ready` (409) — row exists but `generating` / `failed`
 */

const STALE_TIME_MS = 60_000;

export class RoadSenseLifecycleNotFoundError extends Error {
  constructor(message = "Lifecycle not found") {
    super(message);
    this.name = "RoadSenseLifecycleNotFoundError";
  }
}

export class RoadSenseLifecycleNotReadyError extends Error {
  readonly status: "generating" | "failed";
  constructor(status: "generating" | "failed", message = "Lifecycle not ready") {
    super(message);
    this.name = "RoadSenseLifecycleNotReadyError";
    this.status = status;
  }
}

export interface UseRoadSenseLifecycleSitesResult {
  readonly customerName: string | undefined;
  readonly sites: readonly RoadSenseListingSite[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
  readonly isTenantPending: boolean;
}

interface LifecycleSites {
  readonly customerName: string;
  readonly sites: readonly RoadSenseListingSite[];
}

export function useRoadSenseLifecycleSites(reportId: string | undefined): UseRoadSenseLifecycleSitesResult {
  const apiConfig = useApiConfig();
  const tenantId = useTenantId();
  const { isLoading: isTenantLoading } = useTenant();

  // Reuse the by-id endpoint's query key so this shares a cache entry with the
  // report-detail fetch's row read — the same row backs both screens.
  const keyOptions =
    reportId !== undefined ?
      reportItemByIdQuery(apiConfig, { type: "roadsense", reportId })
    : { queryKey: [] as const };

  const query = useQuery({
    queryKey: reportId !== undefined ? [...keyOptions.queryKey, "sites"] : [],
    queryFn:
      apiConfig.tenant && reportId !== undefined ?
        ({ signal }) => fetchLifecycleSites(apiConfig, reportId, signal)
      : skipToken,
    enabled: !!tenantId && reportId !== undefined,
    staleTime: STALE_TIME_MS,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: (failureCount, error) => {
      if (error instanceof RoadSenseLifecycleNotFoundError) return false;
      if (error instanceof RoadSenseLifecycleNotReadyError) return false;
      return failureCount < 3;
    },
  }) as UseQueryResult<LifecycleSites, Error>;

  return {
    customerName: query.data?.customerName,
    sites: query.data?.sites ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ?? null,
    isTenantPending: isTenantLoading || !tenantId,
  };
}

async function fetchLifecycleSites(
  apiConfig: ApiConfig,
  reportId: string,
  signal: AbortSignal | undefined,
): Promise<LifecycleSites> {
  const { tenant, authenticatedFetch } = apiConfig;
  if (!tenant) {
    throw new Error("Tenant not configured");
  }
  const path = expandEndpoint(getReportByIdEndpoint, {
    path: { tenantId: tenant.tenantId, type: "roadsense", reportId },
  });
  const url = `${tenant.apiBaseUrl}${path}`;
  const response = await authenticatedFetch(url, signal ? { signal } : {});
  if (response.status === 404) {
    throw new RoadSenseLifecycleNotFoundError();
  }
  if (response.status === 409) {
    const status = await readNotReadyStatus(response);
    throw new RoadSenseLifecycleNotReadyError(status);
  }
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Request failed: ${String(response.status)}`);
  }
  const body: unknown = await response.json();
  const row = GetReportResponse.parse(body).data;

  // `listingSummary` is an opaque `z.json()` field; the shared decoder owns the
  // one cast and re-validates the structure at runtime.
  const summary = safeDecodeRoadSenseListing(row.listingSummary);
  if (!summary.success) {
    // Drift between the stored listing shape and the schema. Treat as a "not
    // found" rather than a crash — the lifecycle exists but we can't read its
    // sites, so the screen shows an honest empty/error state.
    console.error("RoadSense listingSummary schema mismatch", summary.error);
    throw new RoadSenseLifecycleNotFoundError("Lifecycle listing could not be read");
  }
  return { customerName: summary.data.customerName, sites: summary.data.sites };
}

async function readNotReadyStatus(response: Response): Promise<"generating" | "failed"> {
  try {
    const body = (await response.json()) as { details?: { status?: unknown } };
    const status = body.details?.status;
    if (status === "generating" || status === "failed") return status;
  } catch {
    // Body wasn't JSON or was missing `details.status`. Default to the safer
    // "still working" copy.
  }
  return "generating";
}
